import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import Logger from 'browser-logging/src/browser/Logger';

import { generateUserApiKey } from 'portal/src/api/UsersService';
import GenericDialog from 'portal/src/components/common/GenericDialog';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

const ApiKeyDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const snackbarHandler = useSnackbarHandler();

  const [apiKey, setApiKey] = useState('');

  const {
    mutate: generateNewKey,
    isPending,
    isError,
  } = useMutation({
    mutationFn: async () => {
      const response = await generateUserApiKey();
      const newKey = await response;

      setApiKey(newKey);
    },
  });

  const onCloseDialog = () => {
    setApiKey('');
    onClose();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey).then(
      () => {
        onCloseDialog();
        snackbarHandler.info('API key copied to clipboard');
      },
      (err) => {
        Logger.captureError(err as Error);
        snackbarHandler.error(MESSAGES.GENERIC.ERROR);
      },
    );
  };

  return (
    <GenericDialog
      open={open}
      onClose={onCloseDialog}
      maxWidth="sm"
      dialogActions={
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={onCloseDialog}>
            Close
          </Button>
          <Button
            variant="contained"
            disabled={isPending || isError || !apiKey}
            color="secondary"
            disableElevation
            onClick={handleCopy}
          >
            Copy
          </Button>
        </Stack>
      }
    >
      <Stack justifyContent="center" alignItems="center" width="100%">
        <Card variant="outlined" sx={{ width: '100%' }}>
          <CardHeader title="Generate User API Key" />
          {isError ? (
            <Typography color="error">
              There was an issue generating a new API key.
            </Typography>
          ) : (
            <Typography fontSize="small" sx={{ px: 3 }}>
              Once generated, copy and store the key as it will not be shown
              again once you close this dialog.
            </Typography>
          )}
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            {isPending ? (
              <CircularProgress />
            ) : (
              <>
                <TextField
                  size="small"
                  slotProps={{ input: { readOnly: true } }}
                  value={apiKey}
                  sx={{ width: '100%' }}
                />
                <Button
                  onClick={() => generateNewKey()}
                  disabled={isPending}
                  variant="contained"
                >
                  Generate
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </Stack>
    </GenericDialog>
  );
};

export default ApiKeyDialog;
